import { ArrowUpRight } from "lucide-react";

import { CurrencyText, TranslatedText } from "@/components/common/text";
import { ConfidenceLevel } from "@/components/common/svgs/ConfidenceLevel";
import { LockedLink } from "@/components/common/links/LockedLink";
import { useDispatch, useSelector } from "react-redux";
import { getLocationString } from "@/utils/text";
import { Hr } from "@/components/common/other/Hr";
import { generateLoadboardUrl, generateLoadRatesUrl } from "@/utils/urls";
import { setCurrentSearch } from "@/state/currentSearchSlice";

export const BestOutboundCardWithLoad = ({ index, outbound, withExtendedMarket }) => {
    const dispatch = useDispatch();
    const { trailerType, pickup: currentPickup, pickupDate, pickupRadius } = useSelector((state) => state.currentSearch);
    const { pickupName, pickupZip, deliveryName, deliveryZip, avgPrice: rate, avgRPM: rpm, confidence: volume, distance } = outbound;
    const pickupNameSplit = pickupName.split(", ") || ["", ""];
    const deliveryNameSplit = deliveryName.split(", ") || ["", ""];
    const pickup = { zip: pickupZip, countryCode: "US", placeName: pickupNameSplit[0], stateCode: pickupNameSplit[1] };
    const delivery = { zip: deliveryZip, countryCode: "US", placeName: deliveryNameSplit[0], stateCode: deliveryNameSplit[1] };

    const handleClick = () => {
        dispatch(
            setCurrentSearch({
                trailerType,
                pickupDate,
                pickup,
                delivery,
                pickupQuery: getLocationString(pickup, false),
                deliveryQuery: getLocationString(delivery, false),
            }),
        );
    };

    const urlParams = {
        trailerType,
        pickup,
        pickupQuery: getLocationString(pickup, false),
        pickupDate,
        delivery,
        deliveryQuery: getLocationString(delivery, false),
        deliveryDate: null,
    };

    const loadboardUrl = generateLoadboardUrl({ ...urlParams, pickup: currentPickup, pickupRadius, pickupMarket: pickupName });

    const loadSearchUrl = generateLoadRatesUrl(urlParams);
    return (
        <div className="flex flex-row h-[104px] border border-cm-p-outline rounded-lg bg-cm-p-surface hover:bg-cm-p-surface-hover overflow-hidden">
            <div className="w-[22px] pt-2 bg-cm-p-container-green text-cm-p-on-container-green font-bold flex justify-center text-sm/[22px]">
                {index + 1}
            </div>
            <div className="flex flex-col w-[calc(100%-22px)] pl-[10px] pr-3 py-2 h-full">
                <div className="flex flex-row h-[22px] w-full gap-2 items-center mb-[2px]">
                    <div className="flex flex-1 items-center text-cm-p-text-primary text-sm/[22px] gap-2">
                        {withExtendedMarket ? (
                            <>
                                <div>{pickupName}</div>
                                <div className="w-2 h-[1px] bg-cm-p-text-secondary"></div>
                            </>
                        ) : null}
                        <div>{deliveryName}</div>
                    </div>
                    <div className="flex items-center justify-center h-4 w-4">
                        <ConfidenceLevel level={volume === "high" ? 3 : volume === "medium" ? 2 : 1} />
                    </div>
                </div>
                <div className="flex flex-row items-center justify-between text-sm/[22px]">
                    <div className="w-[56px] text-left">
                        <CurrencyText num={rate} round className="text-cm-p-text-primary text-sm leading-[22px] font-semibold" />
                    </div>
                    <div className="w-[1px] h-3 bg-cm-p-outline" />
                    <div className="w-[48px] text-right">
                        <CurrencyText num={rpm} className="text-cm-p-text-primary text-sm leading-[22px] font-semibold" />
                    </div>
                    <div className="w-[1px] h-3 bg-cm-p-outline" />
                    <div className="w-[117px] text-cm-p-text-secondary text-right">
                        {Math.round(distance)} {outbound.deadheadDistance ? `(${Math.round(outbound.deadheadDistance)} DH)` : null} mi
                    </div>
                </div>
                <Hr className="my-[8px]" />
                <div className="flex flex-row justify-between text-sm/[22px] text-cm-p-text-secondary">
                    <LockedLink to={loadSearchUrl} onClick={handleClick} className="inline-flex items-center gap-[2px]">
                        <TranslatedText>Load details</TranslatedText>
                        <ArrowUpRight className="w-4 h-4 text-cm-p-accent" />
                    </LockedLink>
                    {outbound.numberOfLoads ? (
                        <LockedLink to={loadboardUrl} className="inline-flex flex-row items-center">
                            <TranslatedText className="mr-[2px]">View</TranslatedText>
                            <span className="mr-[2px]">{outbound.numberOfLoads}</span>
                            <TranslatedText>{`load${outbound.numberOfLoads > 1 ? "s" : ""}`}</TranslatedText>
                            <ArrowUpRight className="w-4 h-4 text-cm-p-accent" />
                        </LockedLink>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
